import {
  Card,
  Grow,
  Zoom,
  Typography,
  Grid,
  Button,
  List,
  Box,
  CardContent,
  CardActions,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Alert,
  Tooltip,
} from "@mui/material";
import React, { FC } from "react";
import { useNavigate } from "react-router-dom";

import { useAppSelector } from "../../store";
import { fetchedPlayer } from "../../features/player/playerSlice";
import {
  Badge,
  Class,
  Groups,
  PermIdentity,
  Stars,
  Wc,
} from "@mui/icons-material";

interface RegisterLicenceValidationProps {}

const RegisterLicenceValidation: FC<RegisterLicenceValidationProps> = () => {
  const navigate = useNavigate();
  const player = useAppSelector(fetchedPlayer);

  const handleValidate = async () => {
    navigate("/register/select");
  };

  return (
    <Zoom in>
      <Grid
        container
        direction="column"
        justifyContent="center"
        alignItems="center"
        spacing={2}
      >
        <Grid item>
          <Box display="inline-block">
            <Alert
              severity="info"
              variant="standard"
              icon={false}
              sx={{
                bgcolor: "secondary.light",
                border: "1px solid",
                borderColor: "divider",
                color: "text.primary",
                fontWeight: 500,
                px: 3,
                py: 2,
                boxShadow: 1,
                maxWidth: 480,
                textAlign: "center",
              }}
            >
              Veuillez vérifier que les informations correspondent bien au
              joueur que vous voulez inscrire
            </Alert>
          </Box>
        </Grid>
        <Grid item>
          <Box display="flex" justifyContent="center" width="100%" px={2}>
            <Grow in>
              <Card
                elevation={4}
                sx={{
                  borderRadius: 3,
                  overflow: "hidden",
                  maxWidth: 480,
                  boxShadow: 3,
                  mb: 2,
                }}
              >
                <CardContent sx={{ minWidth: 300 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      color: "text.primary",
                      fontWeight: 600,
                      mb: 2,
                    }}
                  >
                    🧍 Informations du joueur
                  </Typography>
                  <List>
                    <ListItem
                      sx={{
                        py: 1.5,
                        px: 2,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        "&:nth-of-type(odd)": {
                          bgcolor: 'secondary.light + "33"',
                        },
                        "&:hover": { bgcolor: 'primary.light + "22"' },
                      }}
                    >
                      <ListItemAvatar>
                        <Tooltip title="Nom" placement="top-end" arrow>
                          <Avatar
                            sx={{
                              bgcolor: "primary.main",
                              color: "primary.contrastText",
                            }}
                          >
                            <PermIdentity />
                          </Avatar>
                        </Tooltip>
                      </ListItemAvatar>
                      <ListItemText
                        primary={`${player!.name} ${player!.surname}`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemAvatar>
                        <Tooltip
                          title="Numéro de licence"
                          placement="top-end"
                          arrow
                        >
                          <Avatar sx={{ bgcolor: "primary.light" }}>
                            <Badge />
                          </Avatar>
                        </Tooltip>
                      </ListItemAvatar>
                      <ListItemText
                        primaryTypographyProps={{
                          fontWeight: 500,
                          color: "text.primary",
                        }}
                        primary={player!.licenceId}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemAvatar>
                        <Tooltip title="Club" placement="top-end" arrow>
                          <Avatar sx={{ bgcolor: "primary.light" }}>
                            <Groups />
                          </Avatar>
                        </Tooltip>
                      </ListItemAvatar>
                      <ListItemText primary={player!.club} />
                    </ListItem>
                    <ListItem>
                      <ListItemAvatar>
                        <Tooltip
                          title="Points sur la licence"
                          placement="top-end"
                          arrow
                        >
                          <Avatar sx={{ bgcolor: "primary.light" }}>
                            <Stars />
                          </Avatar>
                        </Tooltip>
                      </ListItemAvatar>
                      <ListItemText primary={`${player!.points} Points`} />
                    </ListItem>
                    <ListItem>
                      <ListItemAvatar>
                        <Tooltip title="Genre" placement="top-end" arrow>
                          <Avatar sx={{ bgcolor: "primary.light" }}>
                            <Wc />
                          </Avatar>
                        </Tooltip>
                      </ListItemAvatar>
                      <ListItemText
                        primary={player!.sexe === "M" ? "Homme" : "Femme"}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemAvatar>
                        <Tooltip
                          title="Catégorie d'age"
                          placement="top-end"
                          arrow
                        >
                          <Avatar sx={{ bgcolor: "primary.light" }}>
                            <Class />
                          </Avatar>
                        </Tooltip>
                      </ListItemAvatar>
                      <ListItemText primary={player!.categorie} />
                    </ListItem>
                  </List>
                </CardContent>
                <CardActions>
                  <Grid
                    container
                    alignItems="center"
                    justifyContent="center"
                    sx={{ paddingBottom: "16px" }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleValidate}
                      sx={{
                        mt: 2,
                        px: 4,
                        py: 1.5,
                        fontWeight: 600,
                        textTransform: "none",
                        transition: "transform 0.3s ease",
                        "&:hover": { transform: "translateY(-2px)" },
                      }}
                    >
                      Il s'agit bien de moi
                    </Button>
                  </Grid>
                </CardActions>
              </Card>
            </Grow>
          </Box>
        </Grid>
      </Grid>
    </Zoom>
  );
};

export default RegisterLicenceValidation;
